import { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { 
  User, Calendar, DollarSign, Bell, Loader2, Bus, MapPin,
  BookOpen, Clock, CheckCircle, XCircle, AlertCircle, ArrowRight,
  TrendingUp, GraduationCap, FileText, CreditCard, Navigation,
  Wifi, Phone, ChevronRight
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fix leaflet icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const busIcon = new L.DivIcon({
  className: 'custom-bus-icon',
  html: `<div style="background: #3b82f6; width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
      <path d="M8 6v6"/><path d="M15 6v6"/><path d="M2 12h19.6"/>
      <path d="M18 18h3s.5-1.7.8-2.8c.1-.4.2-.8.2-1.2 0-.4-.1-.8-.2-1.2l-1.4-5C20.1 6.8 19.1 6 18 6H4a2 2 0 0 0-2 2v10h3"/>
      <circle cx="7" cy="18" r="2"/><path d="M9 18h5"/><circle cx="16" cy="18" r="2"/>
    </svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

const ParentDashboard = () => {
  const { token, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeChild, setActiveChild] = useState(0);
  const [stats, setStats] = useState({
    children_count: 0,
    children: [],
    attendance_rate: 0,
    pending_fees: 0,
    unread_notifications: 0,
    hostel_info: [],
    transport_info: []
  });
  const [busLocation, setBusLocation] = useState(null);
  const [transportInfo, setTransportInfo] = useState(null);
  const [hostelInfo, setHostelInfo] = useState(null);
  const [recentAttendance, setRecentAttendance] = useState([]);
  const [pendingHomework, setPendingHomework] = useState([]);
  const [feeDetails, setFeeDetails] = useState([]);

  useEffect(() => {
    fetchDashboard();
  }, [token]);

  useEffect(() => {
    if (stats.children.length > 0) {
      fetchChildDetails(stats.children[activeChild]);
      // Set hostel and transport info from dashboard response
      const childId = stats.children[activeChild]?.id;
      if (childId) {
        const childHostel = stats.hostel_info?.find(h => h.student_id === childId);
        const childTransport = stats.transport_info?.find(t => t.student_id === childId);
        setHostelInfo(childHostel || null);
        setTransportInfo(childTransport || null);
        if (childTransport?.vehicle_id) {
          fetchBusLocation(childTransport.vehicle_id);
        }
      }
    }
  }, [activeChild, stats.children, stats.hostel_info, stats.transport_info]);

  // Auto-refresh bus location every 15 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (transportInfo?.vehicle_id) {
        fetchBusLocation(transportInfo.vehicle_id);
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [transportInfo]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/students/parent/dashboard`, {
        headers: getAuthHeader()
      });
      setStats(response.data);
      if (response.data.children?.length > 0) {
        fetchChildDetails(response.data.children[0]);
      }
    } catch (error) {
      console.error('Failed to fetch parent dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChildDetails = async (child) => {
    if (!child?.id) return;
    
    try {
      // Fetch transport info
      const transportRes = await axios.get(`${API_URL}/api/transport-v2/student/${child.id}`, {
        headers: getAuthHeader()
      }).catch(() => ({ data: null }));
      
      if (transportRes.data) {
        setTransportInfo(transportRes.data);
        if (transportRes.data.vehicle_id) {
          fetchBusLocation(transportRes.data.vehicle_id);
        }
      }

      // Fetch recent attendance (last 7 days)
      const attendanceRes = await axios.get(`${API_URL}/api/attendance/student/${child.id}/recent`, {
        headers: getAuthHeader()
      }).catch(() => ({ data: [] }));
      setRecentAttendance(attendanceRes.data || []);

      // Fetch pending homework
      const homeworkRes = await axios.get(`${API_URL}/api/homework/student/${child.id}/pending`, {
        headers: getAuthHeader()
      }).catch(() => ({ data: [] }));
      setPendingHomework(homeworkRes.data || []);

      // Fetch fee details
      const feeRes = await axios.get(`${API_URL}/api/fee-management/student/${child.id}`, {
        headers: getAuthHeader()
      }).catch(() => ({ data: [] }));
      setFeeDetails(feeRes.data?.items || feeRes.data?.invoices || feeRes.data || []);

    } catch (error) {
      console.error('Failed to fetch child details:', error);
    }
  };

  const fetchBusLocation = async (vehicleId) => {
    try {
      const res = await axios.get(`${API_URL}/api/transport-v2/gps/vehicle/${vehicleId}`, {
        headers: getAuthHeader()
      });
      if (res.data?.vehicle?.current_location) {
        setBusLocation(res.data.vehicle);
      }
    } catch (error) {
      console.error('Failed to fetch bus location');
    }
  };

  const formatLastUpdate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return date.toLocaleTimeString();
  };

  const getAttendanceIcon = (status) => {
    switch (status) {
      case 'present': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'absent': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'late': return <Clock className="h-4 w-4 text-yellow-500" />;
      default: return <AlertCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Parent Dashboard">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  const currentChild = stats.children[activeChild];

  return (
    <DashboardLayout title="Parent Dashboard">
      <div className="space-y-6">
        {/* Child Selector (if multiple children) */}
        {stats.children.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-2">
            {stats.children.map((child, idx) => (
              <Button
                key={idx}
                variant={activeChild === idx ? 'default' : 'outline'}
                onClick={() => setActiveChild(idx)}
                className="flex items-center gap-2 whitespace-nowrap"
              >
                <User className="h-4 w-4" />
                {child.name}
              </Button>
            ))}
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium opacity-90">My Children</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats.children_count}</div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium opacity-90">Attendance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats.attendance_rate}%</div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className={`bg-gradient-to-br ${stats.pending_fees > 0 ? 'from-red-500 to-red-600' : 'from-emerald-500 to-emerald-600'} text-white`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium opacity-90">Pending Fees</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">₹{stats.pending_fees.toLocaleString()}</div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium opacity-90">Notifications</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats.unread_notifications}</div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Main Content - Current Child */}
        {currentChild && (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Child Info & Bus Tracking */}
            <div className="lg:col-span-2 space-y-6">
              {/* Child Info Card */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                        <User className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <CardTitle>{currentChild.name}</CardTitle>
                        <CardDescription>
                          {currentChild.class_name} • Roll: {currentChild.roll_number || currentChild.student_id}
                        </CardDescription>
                      </div>
                    </div>
                    <Link to={`/parent/child/${currentChild.id}`}>
                      <Button variant="outline" size="sm">
                        View Profile <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </CardHeader>
              </Card>

              {/* Bus Tracking - Only show if transport is allocated */}
              {transportInfo && (
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Bus className="h-5 w-5" /> Bus Tracking
                    </CardTitle>
                    {busLocation?.is_tracking ? (
                      <Badge className="bg-green-500">
                        <Wifi className="h-3 w-3 mr-1" /> Live
                      </Badge>
                    ) : (
                      <Badge variant="secondary">Offline</Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Transport Info */}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-muted-foreground">Vehicle</p>
                        <p className="font-medium">{transportInfo.vehicle_number || 'N/A'}</p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-muted-foreground">Route</p>
                        <p className="font-medium">{transportInfo.route_name || 'N/A'}</p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-muted-foreground">Pickup Stop</p>
                        <p className="font-medium">{transportInfo.pickup_stop || transportInfo.pickup_stop_name || 'N/A'}</p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-muted-foreground">Pickup Time</p>
                        <p className="font-medium">{transportInfo.pickup_time || 'N/A'}</p>
                      </div>
                    </div>

                    {/* Map */}
                    {busLocation?.current_location ? (
                      <div className="h-[250px] rounded-lg overflow-hidden border">
                        <MapContainer
                          center={[busLocation.current_location.lat, busLocation.current_location.lng]}
                          zoom={15}
                          style={{ height: '100%', width: '100%' }}
                        >
                          <TileLayer
                            attribution='&copy; OpenStreetMap'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          />
                          <Marker
                            position={[busLocation.current_location.lat, busLocation.current_location.lng]}
                            icon={busIcon}
                          >
                            <Popup>
                              <div className="text-sm">
                                <p className="font-bold">{busLocation.vehicle_number}</p>
                                <p>Speed: {busLocation.current_location.speed_kmh || 0} km/h</p>
                                <p className="text-xs">Updated: {formatLastUpdate(busLocation.last_location_update)}</p>
                              </div>
                            </Popup>
                          </Marker>
                        </MapContainer>
                      </div>
                    ) : (
                      <div className="h-[200px] bg-muted rounded-lg flex items-center justify-center">
                        <div className="text-center text-muted-foreground">
                          <MapPin className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>Bus location not available</p>
                          <p className="text-xs">Tracking will appear when bus is on route</p>
                        </div>
                      </div>
                    )}

                    {/* Driver Contact */}
                    {transportInfo.driver_phone && (
                      <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-blue-600" />
                          <span className="text-sm">Driver: {transportInfo.driver_name}</span>
                        </div>
                        <Button size="sm" variant="outline" asChild>
                          <a href={`tel:${transportInfo.driver_phone}`}>Call</a>
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              )}

              {/* Recent Attendance */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5" /> Recent Attendance
                    </CardTitle>
                    <Link to="/parent/attendance">
                      <Button variant="ghost" size="sm">View All</Button>
                    </Link>
                  </div>
                </CardHeader>
                <CardContent>
                  {recentAttendance.length > 0 ? (
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {recentAttendance.slice(0, 7).map((record, idx) => (
                        <div
                          key={idx}
                          className={`flex flex-col items-center p-3 rounded-lg min-w-[70px] ${
                            record.status === 'present' ? 'bg-green-50 dark:bg-green-900/20' :
                            record.status === 'absent' ? 'bg-red-50 dark:bg-red-900/20' :
                            'bg-yellow-50 dark:bg-yellow-900/20'
                          }`}
                        >
                          {getAttendanceIcon(record.status)}
                          <span className="text-xs mt-1 font-medium">
                            {new Date(record.date).toLocaleDateString('en-US', { weekday: 'short' })}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(record.date).getDate()}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center py-4 text-muted-foreground">No recent attendance records</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Homework & Fees */}
            <div className="space-y-6">
              {/* Pending Homework */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <BookOpen className="h-5 w-5" /> Pending Homework
                    </CardTitle>
                    <Badge variant="secondary">{pendingHomework.length}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {pendingHomework.length > 0 ? (
                    <div className="space-y-3">
                      {pendingHomework.slice(0, 5).map((hw, idx) => (
                        <div key={idx} className="p-3 bg-muted/50 rounded-lg">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-sm">{hw.title}</p>
                              <p className="text-xs text-muted-foreground">{hw.subject}</p>
                            </div>
                            <Badge variant={new Date(hw.due_date) < new Date() ? 'destructive' : 'outline'} className="text-xs">
                              {new Date(hw.due_date).toLocaleDateString()}
                            </Badge>
                          </div>
                        </div>
                      ))}
                      <Link to="/homework">
                        <Button variant="ghost" size="sm" className="w-full">
                          View All Homework <ArrowRight className="h-4 w-4 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="text-center py-6 text-muted-foreground">
                      <CheckCircle className="h-10 w-10 mx-auto mb-2 text-green-500 opacity-50" />
                      <p className="text-sm">No pending homework!</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Fee Summary */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <CreditCard className="h-5 w-5" /> Fee Summary
                    </CardTitle>
                    <Link to="/fee-management">
                      <Button variant="ghost" size="sm">Pay Now</Button>
                    </Link>
                  </div>
                </CardHeader>
                <CardContent>
                  {feeDetails.length > 0 ? (
                    <div className="space-y-3">
                      {feeDetails.filter(f => f.status === 'pending' || f.status === 'partial').slice(0, 3).map((fee, idx) => (
                        <div key={idx} className="p-3 bg-muted/50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-medium text-sm">{fee.fee_type || fee.type}</p>
                            <Badge variant={fee.status === 'pending' ? 'destructive' : 'secondary'}>
                              {fee.status}
                            </Badge>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Amount</span>
                            <span className="font-medium">₹{(fee.amount || 0).toLocaleString()}</span>
                          </div>
                          {fee.due_date && (
                            <div className="flex justify-between text-xs mt-1">
                              <span className="text-muted-foreground">Due Date</span>
                              <span>{new Date(fee.due_date).toLocaleDateString()}</span>
                            </div>
                          )}
                        </div>
                      ))}
                      
                      {stats.pending_fees > 0 && (
                        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-red-700 dark:text-red-400">Total Pending</span>
                            <span className="text-lg font-bold text-red-700 dark:text-red-400">
                              ₹{stats.pending_fees.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-muted-foreground">
                      <CheckCircle className="h-10 w-10 mx-auto mb-2 text-green-500 opacity-50" />
                      <p className="text-sm">All fees paid!</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Hostel Info - Only show if hostel is allocated */}
              {hostelInfo && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 21h18"/>
                        <path d="M5 21V7l8-4v18"/>
                        <path d="M19 21V11l-6-3"/>
                        <path d="M9 9v.01"/>
                        <path d="M9 12v.01"/>
                        <path d="M9 15v.01"/>
                        <path d="M9 18v.01"/>
                      </svg>
                      Hostel Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-muted/50 rounded-lg">
                          <p className="text-xs text-muted-foreground">Building</p>
                          <p className="font-medium text-sm">{hostelInfo.building_name || 'N/A'}</p>
                        </div>
                        <div className="p-3 bg-muted/50 rounded-lg">
                          <p className="text-xs text-muted-foreground">Room</p>
                          <p className="font-medium text-sm">{hostelInfo.room_number || 'N/A'}</p>
                        </div>
                        <div className="p-3 bg-muted/50 rounded-lg">
                          <p className="text-xs text-muted-foreground">Bed</p>
                          <p className="font-medium text-sm">{hostelInfo.bed_number || 'N/A'}</p>
                        </div>
                        <div className="p-3 bg-muted/50 rounded-lg">
                          <p className="text-xs text-muted-foreground">Monthly Fee</p>
                          <p className="font-medium text-sm">₹{(hostelInfo.monthly_fee || 0).toLocaleString()}</p>
                        </div>
                      </div>
                      
                      {hostelInfo.warden_name && (
                        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-muted-foreground">Warden</p>
                              <p className="font-medium text-sm">{hostelInfo.warden_name}</p>
                            </div>
                            {hostelInfo.warden_phone && (
                              <Button size="sm" variant="outline" asChild>
                                <a href={`tel:${hostelInfo.warden_phone}`}>
                                  <Phone className="h-4 w-4 mr-1" /> Call
                                </a>
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Transport Summary - Only show if transport is allocated */}
              {transportInfo && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Bus className="h-5 w-5" /> Transport Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-muted/50 rounded-lg">
                          <p className="text-xs text-muted-foreground">Route</p>
                          <p className="font-medium text-sm">{transportInfo.route_name || 'N/A'}</p>
                        </div>
                        <div className="p-3 bg-muted/50 rounded-lg">
                          <p className="text-xs text-muted-foreground">Vehicle</p>
                          <p className="font-medium text-sm">{transportInfo.vehicle_number || 'N/A'}</p>
                        </div>
                        <div className="p-3 bg-muted/50 rounded-lg">
                          <p className="text-xs text-muted-foreground">Pickup</p>
                          <p className="font-medium text-sm">{transportInfo.pickup_stop || 'N/A'}</p>
                        </div>
                        <div className="p-3 bg-muted/50 rounded-lg">
                          <p className="text-xs text-muted-foreground">Monthly Fee</p>
                          <p className="font-medium text-sm">₹{(transportInfo.monthly_fee || 0).toLocaleString()}</p>
                        </div>
                      </div>
                      
                      {transportInfo.driver_name && (
                        <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-muted-foreground">Driver</p>
                              <p className="font-medium text-sm">{transportInfo.driver_name}</p>
                            </div>
                            {transportInfo.driver_phone && (
                              <Button size="sm" variant="outline" asChild>
                                <a href={`tel:${transportInfo.driver_phone}`}>
                                  <Phone className="h-4 w-4 mr-1" /> Call
                                </a>
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Quick Links */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Quick Links</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-2">
                  <Link to="/parent/attendance">
                    <Button variant="outline" className="w-full justify-start">
                      <Calendar className="h-4 w-4 mr-2" /> Attendance
                    </Button>
                  </Link>
                  <Link to="/parent/results">
                    <Button variant="outline" className="w-full justify-start">
                      <TrendingUp className="h-4 w-4 mr-2" /> Results
                    </Button>
                  </Link>
                  <Link to="/homework">
                    <Button variant="outline" className="w-full justify-start">
                      <BookOpen className="h-4 w-4 mr-2" /> Homework
                    </Button>
                  </Link>
                  <Link to="/gps-tracking">
                    <Button variant="outline" className="w-full justify-start">
                      <Bus className="h-4 w-4 mr-2" /> Track Bus
                    </Button>
                  </Link>
                  <Link to="/fee-management">
                    <Button variant="outline" className="w-full justify-start">
                      <CreditCard className="h-4 w-4 mr-2" /> Pay Fees
                    </Button>
                  </Link>
                  <Link to="/notices">
                    <Button variant="outline" className="w-full justify-start">
                      <Bell className="h-4 w-4 mr-2" /> Notices
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* No Children State */}
        {stats.children.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <User className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-2">No Children Linked</h3>
              <p className="text-muted-foreground">Contact school administration to link your children to your account.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ParentDashboard;
